import apiClient, { requiresAuth } from "./apiClient";
import { ParticipantData } from "../models/Participant";
import axios, { AxiosHeaders, AxiosRequestHeaders } from "axios";
import { Registration, RegistrationStatus } from "../models/Registration";

class RegistrationService {
  async registerForCourse(
    courseId: number | null,
    participantData: ParticipantData | null,
    registrationCode?: string
  ) {
    const url = courseId
      ? `/courses/${courseId}/register`
      : `/courses/register-with-code?code=${registrationCode}`;

    const formData = new FormData();

    if (participantData) {
      (Object.keys(participantData) as Array<keyof ParticipantData>).forEach(
        (key) => {
          const value = participantData[key];
          if (value !== undefined && value !== null) {
            formData.append(key, value.toString());
          }
        }
      );
    }

    if (registrationCode) {
      formData.append("registrationCode", registrationCode);
    }

    // Let errors propagate to the caller for proper handling
    const response = await apiClient.post(url, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  }

  async validateRegistrationCode(code: string) {
    try {
      const response = await apiClient.get(`/courses/validate-code`, {
        params: { code },
      });
      return {
        status: response.status,
        data: response.data,
      };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return {
          status: error.response.status,
          data: error.response.data,
        };
      }
      throw error;
    }
  }

  async getParticipantData(invitationId: string): Promise<ParticipantData> {
    try {
      const response = await apiClient.get(
        `/participants/get-by-invitation/${invitationId}`,
        requiresAuth()
      );
      return response.data;
    } catch (error) {
      console.error("Failed to fetch participant data:", error);
      throw new Error("Failed to fetch participant data");
    }
  }

  async getRegistrationDataForCourseAndParticipant(
    courseId: string,
    participantId: string
  ): Promise<Registration> {
    try {
      const response = await apiClient.get(
        `/courses/${courseId}/participants/${participantId}/registration/`
      );
      return response.data;
    } catch (error) {
      console.error("Failed to fetch registration data:", error);
      throw new Error("Failed to fetch registration data");
    }
  }
  async changeRegistrationStatus(
    courseId: string,
    registrationId: string,
    newStatus: RegistrationStatus
  ): Promise<Registration> {
    try {
      console.log(newStatus)
      const response = await apiClient.patch(
        `/courses/${courseId}/registrations/${registrationId}/status`,
        { newStatus },
        requiresAuth()
      );
      return response.data;
    } catch (error) {
      console.error("Failed to change registration status:", error);
      throw new Error("Failed to change registration status");
    }
  }

  async updateOnlineCourseCompleteByEmail(
    courseId: string,
    registrationId: string,
    pdfBytes: string
  ): Promise<ParticipantData> {
    try {
      const response = await apiClient.patch(
        `/courses/${courseId}/registrations/${registrationId}/complete`,
        { pdfBytes }
      );
      return response.data;
    } catch (error) {
      console.error("Failed to change registration status:", error);
      throw new Error("Failed to change registration status");
    }
  }

  async moveFromWaitlist(
    courseId: string,
    registrationIds: string | string[],
    newStatus: RegistrationStatus
  ): Promise<Registration[]> {
    try {
      const ids = Array.isArray(registrationIds)
        ? registrationIds
        : [registrationIds];
      const response = await apiClient.patch(
        `/courses/${courseId}/registrations/waitlist`,
        { registrationIds: ids, newStatus },
        requiresAuth()
      );
      return response.data;
    } catch (error) {
      console.error("Failed to move registrations from waitlist:", error);
      throw new Error("Failed to move registrations from waitlist");
    }
  }

  async getRegistrationsForCourse(
    courseId: string,
    includeParticipant: boolean = false
  ): Promise<Registration[]> {
    try {
      const params = {
        includeParticipant: includeParticipant,
      };
      const headers = new AxiosHeaders({});

      const config = {
        params: params,
        headers: headers as AxiosRequestHeaders,
      };
      const response = await apiClient.get(
        `/courses/${courseId}/registrations`,
        requiresAuth(config)
      );
      return response.data;
    } catch (error) {
      console.error("Failed to fetch registrations for course:", error);
      throw new Error("Failed to fetch registrations for course");
    }
  }
  async deleteRegistration(
    courseId: number,
    registrationId: string
  ): Promise<void> {
    try {
      await apiClient.delete(
        `/courses/${courseId}/registrations/${registrationId}`,
        requiresAuth()
      );
    } catch (error) {
      console.error("Failed to delete registration:", error);
      throw new Error("Failed to delete registration");
    }
  }
}

export default new RegistrationService();
